import { UI_SCALE } from '../../theme/tokens'
import { ICON_PATHS, type IconName, type IconShape } from './paths'

export type { IconName }

function renderShape(shape: IconShape, key: number): JSX.Element {
  switch (shape.tag) {
    case 'path':
      return <path key={key} d={shape.d} />
    case 'circle':
      return <circle key={key} cx={shape.cx} cy={shape.cy} r={shape.r} />
    case 'rect':
      return (
        <rect
          key={key}
          x={shape.x}
          y={shape.y}
          width={shape.width}
          height={shape.height}
          rx={shape.rx}
          ry={shape.ry}
        />
      )
  }
}

export function Icon({
  name,
  size = 16,
  className = '',
}: {
  readonly name: IconName
  readonly size?: number
  readonly className?: string
}): JSX.Element {
  // Presentation attributes, not a `style` prop: a `style` sets an inline style, which beats
  // every class-selector rule in `global.css` regardless of specificity — including the ones
  // (`.card-glyph`, `.card-icon-glyph`, `.gear-ghost-icon`, …) that exist specifically to
  // override a call site's `size` for one context. An attribute is a presentation attribute,
  // the lowest-priority origin, so those overrides keep working.
  //
  // The scale is resolved *here* rather than left to `calc(${size}px * var(--fs))`, which this
  // file carried until it reached real phones. `var()` inside an SVG **length** presentation
  // attribute resolves only from Chrome 139 (June 2025); an older engine — Samsung Internet
  // above all, whose Chromium base trails by many versions — treats the whole attribute as
  // invalid and **drops** it rather than falling back to `size`, and an `<svg>` carrying a
  // `viewBox` and no width stretches to its container, which `.lucide { flex: none }` then
  // refuses to shrink back. Every icon no class happened to size rendered a screen wide. The
  // same expression in the class overrides above is a real CSS property and was unaffected,
  // which is exactly what made the breakage look selective: one text, two parsers, and the
  // ones that still looked right are why it read as a per-device oddity.
  //
  // Nothing is lost by resolving it at render: `--fs` *is* `UI_SCALE` (`theme/tokens.ts`, held
  // equal by `theme.test.tsx`), baked into the baseline with no setting and no persistence. If
  // `--fs` ever becomes a runtime knob, this goes back through CSS — a `--icon-px` custom
  // property the `.lucide`/`.material` rule reads — never back into the attribute.
  const scaled = `${size * UI_SCALE}px`
  return (
    <svg
      className={`lucide lucide-${name} ${className}`.trim()}
      width={scaled}
      height={scaled}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON_PATHS[name].map((shape, index) => renderShape(shape, index))}
    </svg>
  )
}
